import {
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { MailPlus } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AsyncSection,
  Alert,
  Button,
  Card,
  CardBody,
  Checkbox,
  ConfirmDialog,
  Input,
  Modal,
  Pagination,
  Select,
  StatusPill,
  useCursorPagination,
} from "@playbookai/ui";
import { useAuthStore } from "../../auth/auth-store.js";
import { canAdministerWorkspace } from "../../auth/roles.js";
import {
  sameSession,
  scopeFromClaims,
  type SessionScope,
} from "../../auth/session-scope.js";
import type { TenantRole } from "../../api/admin-users-api.js";
import { useAccessPolicy } from "./AccessPolicyPanel.js";
import { useMailSendingState } from "./useMailSendingState.js";
import {
  createInvite,
  invitesCountQueryKey,
  invitesQueryKey,
  invitesQueryKeyPrefix,
  listInvites,
  revokeInvite,
  validateInviteEmail,
  type CreatedInvite,
  type Invite,
  type InviteFilters,
} from "../../api/invites-api.js";
import { useDateTimeFormatter } from "../../settings/format-date-time.js";
import { AdminTimeZoneLabel } from "./AdminTimeZoneLabel.js";

/* Invite administration (PB-1597 / AC-10), composed INTO Members & roles rather
   than living at its own route.

   The reason is the admin's mental model, not the schema: "give this person
   access" is one job with three answers — they are in the directory (Add
   member), they need a password account (Create local account), or they are not
   in the directory yet (Invite). Splitting the third into a separate nav section
   made the admin classify the person before they could act. Invitations being a
   separate TABLE is a storage fact and has no business shaping navigation.

   Within the page, members and invites are TABS of one directory rather than two
   stacked tables. Stacked, the page carried two search boxes, two status
   filters and two "Load more" buttons, and nothing on screen said which control
   drove which list. One list at a time means one toolbar with one meaning.

   Three parts, deliberately separate:
     * useInviteDialog  — the action. Needed in the page toolbar AND in the
       directory-search empty state, so it cannot live inside the list.
     * usePendingInviteCount — the tab badge. A different question ("is anyone
       waiting?") from the list's ("show me this filtered page"), and it must
       answer while the Members tab is showing and the list is unmounted.
     * InvitesTabPanel  — the list, with its own search, filter and cursor.
   They share only the query key prefix, so creating an invite refreshes the
   badge and the list through cache invalidation rather than prop drilling.

   Admin-side lifecycle only: nothing HERE accepts an invitation or creates an
   account. That still holds — redemption is a separate, PUBLIC page
   (`src/pages/invite/InvitePage.tsx`), because the person redeeming has no
   session and no account yet. What PB-4922 changed is the second half: the
   dialog no longer says the link does not work, because it does — and it shows
   the link ALONGSIDE the invitation email rather than instead of it. Mail is
   the default channel; the show-once link is the fallback for a workspace with
   no configured mail sender, which would otherwise have no way to invite
   anyone. */

const ROLE_OPTIONS: ReadonlyArray<{ value: TenantRole; label: string }> = [
  { value: "tenant-member", label: "Member" },
  { value: "tenant-admin", label: "Admin" },
];

type InviteViewFilter = "pending" | "expired" | "revoked" | "all";

const INVITES_VIEW_PAGE_SIZE = 50;

/* Expired remains derived rather than stored. The UI maps it to the stored
   pending lifecycle plus a server-side expiry predicate evaluated with the
   same request timestamp used to render each row. */
const STATUS_FILTER_OPTIONS: ReadonlyArray<{
  value: InviteViewFilter;
  label: string;
}> = [
  { value: "pending", label: "Pending" },
  { value: "expired", label: "Expired" },
  { value: "revoked", label: "Revoked" },
  { value: "all", label: "All" },
];

const STATUS_TONE = {
  pending: { tone: "watch", label: "Invited" },
  expired: { tone: "critical", label: "Expired" },
  revoked: { tone: "neutral", label: "Revoked" },
  /* PB-2926 widened the stored status to `accepted` and PB-4922 made it
     reachable: redemption writes it. The label landed with the widening rather
     than after it, so nothing here had to change when the writer arrived.
     "Joined" rather than "Accepted": what an administrator wants from this
     column is whether the person is in, not which verb the record used. */
  accepted: { tone: "healthy", label: "Joined" },
} as const;

/* The `InviteStatus` union is exhaustive at compile time, but the value arrives
   from the server at runtime. `accepted` is no longer the hypothetical this
   comment once described — PB-2926 widened the status CHECK and STATUS_TONE now
   carries it — so the fallback exists for the NEXT widening rather than that one.
   Without it the first unrecognised row makes `presentation.label` throw and takes
   the WHOLE list down, not one row. Shown verbatim so an unrecognised status is
   visible rather than silently normalised away. */
function presentationFor(status: string): {
  tone: "watch" | "critical" | "neutral" | "healthy";
  label: string;
} {
  return (
    STATUS_TONE[status as keyof typeof STATUS_TONE] ?? {
      tone: "neutral",
      label: status,
    }
  );
}

/* PB-4922. The invitation route is no longer a choice: every invitation is a
   password account. The corporate-sign-in INVITATION was withdrawn because SCIM
   and the directory picker already provision directory people — an invitation
   added no capability and its link granted nothing.

   Kept as a standalone hint on the dialog rather than as a Select hint, because
   there is no longer a control to hang it on. Without a home it would simply
   vanish, and it is the only place a workspace is told WHY its invite was
   refused. */
const LOCAL_ROUTE_BLOCKED_HINT =
  "New password-account invitations are turned off for this workspace: turn on \u201CAllow new password-account invitations\u201D under Password accounts below to use that route.";

/* The OTHER switch, which this dialog never knew about. The server has always
   had two and gives them deliberately different sentences — winding the type
   down (existing accounts keep working, no new ones issued) is a different
   administrator intent from turning password accounts off outright — but the
   client only ever read `localInvitationsEnabled`, so the harder refusal arrived
   with no forewarning at all.

   It matters more since PB-4922 than it did before. While the corporate
   invitation existed, accounts-off still left a route to invite somebody by; with
   that route withdrawn, accounts-off means this dialog cannot produce anything,
   and saying so before the address is typed is the least it can do. */
const LOCAL_ACCOUNTS_BLOCKED_HINT =
  "Password accounts are turned off for this workspace, so email invitations cannot be created: turn on \u201CAllow password accounts\u201D under Password accounts below. People already in your directory can still be added from Add people.";

function roleLabel(role: string): string {
  return ROLE_OPTIONS.find((option) => option.value === role)?.label ?? role;
}

export interface InviteDialogController {
  /** Opens the dialog, optionally pre-filling the address. */
  readonly openInvite: (prefillEmail?: string) => void;
  /** Render once, anywhere in the page. */
  readonly dialog: ReactNode;
}

/** The whole dialog, stamped with the session AND the open it belongs to. */
interface InviteDialogState {
  /* Unique per open. The scope alone cannot identify a dialog: close and reopen
     within one session produces a different dialog with an identical scope, and
     an in-flight completion must not adopt into it. */
  readonly id: number;
  readonly scope: SessionScope;
  readonly email: string;
  readonly role: TenantRole;
  readonly grantTransformationLead: boolean;
  readonly emailError: string | null;
  /* The created invite, held so its URL can be copied. This is the ONLY place
     the URL exists after the response — the server stores a hash, so closing
     the dialog loses it for good. Hence a distinct stage rather than a toast. */
  readonly created: CreatedInvite | null;
  readonly copied: boolean;
}

/** A selected invitation plus the session and the selection it belongs to. */
interface ScopedInvite {
  /** Unique per selection, for the same reason `InviteDialogState.id` is. */
  readonly id: number;
  readonly scope: SessionScope;
  readonly invite: Invite;
}

/** What is submitted, carrying the dialog and session that authored it. */
interface ScopedInviteDraft {
  readonly dialogId: number;
  readonly scope: SessionScope;
  readonly email: string;
  readonly role: TenantRole;
  readonly grantTransformationLead: boolean;
}

/**
 * Owns the invite dialog so a page can trigger it from more than one place —
 * the toolbar and the directory-search empty state both open the same flow.
 */
export function useInviteDialog(): InviteDialogController {
  const formatter = useDateTimeFormatter();
  const { dateTimeOr } = formatter;
  const queryClient = useQueryClient();
  const claims = useAuthStore((s) => s.claims);
  const scope = scopeFromClaims(claims);
  /* Advisory only — it decides WORDING, never whether the credential renders.
     See `useMailSendingState`: loading and failure both answer `unknown`, which
     takes the branch that claims neither a delivery nor a pause. */
  const sendingState = useMailSendingState();

  /* ONE fenced state object for the whole dialog, not six loose pieces.

     Every part of it belongs to the session that opened it: the draft address
     and role are that admin's intent, and `created` holds the raw show-once
     invite URL — the only copy that exists client-side, since the server stores
     a hash. The `/admin` tree stays mounted across an in-place session handover
     (a `/me` refetch after someone signs in elsewhere), so unfenced state means
     the next administrator can be shown the previous one's invite credential and
     can submit their half-typed draft under a different session.

     Fenced on the full `SessionScope`, not just the tenant: two admins of the
     SAME tenant can replace each other, and the second must not inherit the
     first's credential either. Same fence OrganizationAdmin's draft and
     MembersAdmin's temporary password use.

     Derived rather than cleared in an effect — an effect runs after render, so
     there would be one commit in which the credential is still on screen and
     copyable. This makes that state unrepresentable instead of short-lived. */
  const [dialogState, setDialogState] = useState<InviteDialogState | null>(
    null,
  );
  /* A counter, not a random or time-based value: those are unavailable to the
     hashing scripts' rules elsewhere in the repo and, more to the point, a
     counter is deterministic in tests. Monotonic per hook instance is all the
     uniqueness this needs — ids are only ever compared with ids from the same
     hook. */
  const nextDialogId = useRef(0);
  const active =
    dialogState && sameSession(dialogState.scope, scope) ? dialogState : null;

  /* SYNCHRONOUS callers only — every one of them runs inside an event handler
     while the dialog is rendering, which already proves the scope matches.

     Anything writing AFTER an `await` must use `patchOperation` below. `active` is
     a RENDER fence: it answers "may this be displayed", and it cannot answer "may
     this be written", because an async continuation writes into whichever dialog
     is current by then. If that is a different live dialog — the replacement
     admin's, or this admin's own after a dismiss and reopen — `active` renders the
     foreign write without complaint.

     Rounds 3 and 4 of review were both this, at two different call sites: the
     create completion, then the clipboard continuation. The rule that covers both,
     and the one to apply to any new async write here: name the originating dialog
     and have the WRITE check it against the current state. Where the identity is
     captured matters less than it looks — a render closure is already stable
     across an await — so the check inside the updater is what does the work. */
  const patch = (changes: Partial<Omit<InviteDialogState, "scope">>): void =>
    setDialogState((current) =>
      current ? { ...current, ...changes } : current,
    );

  /* The write half of the fence: applies `changes` only if the dialog on screen is
     still the one `origin` identifies. Every asynchronous continuation goes
     through this — the create completion and both clipboard continuations — so a
     late result addresses its own operation or nothing at all.

     `id` carries the weight: unique per open, so a close-and-reopen inside one
     session cannot inherit it. The scope comparison beside it is redundant given
     that, and kept deliberately — it states the property the guard exists for, and
     deleting a "redundant" credential guard on the strength of a mutation run is
     exactly what caused round 3. */
  const patchOperation = (
    origin: { readonly dialogId: number; readonly scope: SessionScope },
    changes: Partial<Omit<InviteDialogState, "scope">>,
  ): void =>
    setDialogState((current) =>
      current &&
      current.id === origin.dialogId &&
      sameSession(current.scope, origin.scope)
        ? { ...current, ...changes }
        : current,
    );

  /* PB-4826. Shared read with the panel and the create-account button — one
     fetch, one key. Undefined while loading or on failure leaves the route
     OFFERED: the server refuses it authoritatively, so an unreadable policy must
     not be what stops an administrator inviting someone. */
  const { policy: accessPolicy } = useAccessPolicy();
  /* Fails OPEN, and that is correct HERE specifically: the server enforces this
     route (`assertRoutePermitted`), so offering it while the policy is unknown
     costs a refused request and nothing else. The create-account gate on
     MembersAdmin deliberately does the opposite, because nothing on the server
     enforces that one — see the note on `useAccessPolicy`. */
  const localInvitesAllowed = accessPolicy?.localInvitationsEnabled !== false;
  const localAccountsAllowed = accessPolicy?.localAccountsEnabled !== false;
  /* Undefined while the policy is still loading, and that is the point: BOTH
     read `!== false`, so a pending fetch neither blocks the dialog nor claims a
     switch is on. Accounts is reported first because it is the wider refusal —
     with it off, the invitation switch cannot matter. */
  const blockedReason = !localAccountsAllowed
    ? LOCAL_ACCOUNTS_BLOCKED_HINT
    : !localInvitesAllowed
      ? LOCAL_ROUTE_BLOCKED_HINT
      : null;
  /* The response is handed over by ref instead of being returned, and that is a
     credential-lifetime decision rather than a style one.

     `CreatedInvite` carries `inviteUrl`, which embeds the raw token — the server
     hands it over exactly once and stores only its hash. Whatever a `mutationFn`
     RETURNS becomes `Mutation.state.data` in TanStack's MutationCache, and
     `create.reset()` does not reach it: reset detaches the observer, it does not
     erase the mutation, which then sits unobserved until the cache's GC window
     elapses. So returning it put a second copy of a live invite URL in the shared
     QueryClient, readable long after the dialog closed and after a session
     handover — contradicting the invariant the rest of this fence rests on, that
     the dialog container is the only place the URL exists after the response.

     Keyed by the submitting dialog rather than held in one slot, because two
     creations can genuinely overlap: a handover does not cancel the previous
     administrator's request, and the replacement can start their own before it
     settles. Both `mutationFn`s would then resume before either `onSuccess` runs,
     a single slot would let the later response overwrite the earlier one, the
     dialog-id fence would reject the mismatched pair, and the second
     administrator would be left with no URL for an invite the server had already
     created.

     Recorded as docs/learnings/2026-08-12-mutation-cache-holds-show-once-credentials.md,
     which named this file as the known open instance. Fixed here alongside
     PB-1585's own show-once surface rather than left as a third copy of the same
     defect. */

  const deliveredInvites = useRef(new Map<number, CreatedInvite>());
  const create = useMutation({
    mutationFn: async (draft: ScopedInviteDraft): Promise<void> => {
      deliveredInvites.current.set(
        draft.dialogId,
        await createInvite(
          draft.email,
          draft.role,
          draft.grantTransformationLead,
        ),
      );
    },
    onError: (_error, variables) => {
      /* A failed request writes no entry, so this clears nothing today. It is
         here because the map must not depend on which terminal path a request
         takes to stay bounded — every operation removes its own key. */
      deliveredInvites.current.delete(variables.dialogId);
    },
    onSuccess: (_result, variables) => {
      /* The invite is COMMITTED either way, so the origin tenant's cached lists
         and badge are stale and must be refreshed. Keyed on the ORIGIN tenant
         carried in `variables`, never on a tenant read from the component — a
         render closure lags a handover by a commit, and a live store read would
         name the REPLACEMENT tenant. Either one leaves the tenant that actually
         gained an invite showing a stale list and badge, and refetches an
         unrelated tenant instead.

         The PREFIX, so every filter combination the admin has visited is
         refreshed along with the tab badge; invalidating one filtered key would
         leave a stale page waiting behind the filter dropdown. */
      void queryClient.invalidateQueries({
        queryKey: invitesQueryKeyPrefix(variables.scope?.tenantId),
      });
      /* Adopted ONLY by the dialog that submitted it. The id is what makes this
         sound: it is unique per open, so a close-and-reopen inside one session —
         reachable with Escape or the header X, neither of which is gated on the
         pending state — cannot inherit an earlier request's credential, and
         neither can a replacement administrator's dialog.

         The scope comparison is redundant given that (a dialog's scope never
         changes after open, so equal ids imply equal scopes) and is kept anyway:
         it states the property the guard exists for, and the last round of this
         review was caused by deleting a "redundant" credential guard on the
         strength of a mutation run. Being unpinned means the tests are
         incomplete, not that the guard is dead.

         Anything else is dropped unshown. Losing the URL is recoverable — revoke
         and re-invite — whereas showing it to the wrong dialog is not. */
      /* Read and removed together, so the slot never outlives the commit that
         consumes it. The undefined branch is unreachable — `mutationFn` writes
         this key before it resolves — and narrows the type rather than guarding
         anything. */
      const created = deliveredInvites.current.get(variables.dialogId);
      deliveredInvites.current.delete(variables.dialogId);
      if (created) {
        patchOperation(variables, { created, copied: false });
      }
    },
  });

  const close = (): void => {
    setDialogState(null);
    create.reset();
  };

  const openInvite = (prefillEmail?: string): void => {
    nextDialogId.current += 1;
    setDialogState({
      id: nextDialogId.current,
      scope,
      email: prefillEmail ?? "",
      role: "tenant-member",
      grantTransformationLead: false,
      emailError: null,
      created: null,
      copied: false,
    });
    create.reset();
  };

  const submit = (): void => {
    if (!active) return;
    const problem = validateInviteEmail(active.email);
    patch({ emailError: problem });
    if (problem) return;
    create.mutate({
      dialogId: active.id,
      scope: active.scope,
      email: active.email.trim(),
      role: active.role,
      grantTransformationLead: active.grantTransformationLead,
    });
  };

  /* A mutation's `isPending` and `error` belong to the component, not to the
     session that started it — the same reasoning as OrganizationAdmin's save.
     Read through the mutation's own variables so both retire with the session
     that owns them, rather than disabling the next admin's form or reporting a
     failure over a dialog they never opened.

     This and the `create.reset()` in `openInvite`/`close` are two routes to the
     same guarantee, and either alone is sufficient — a mutation run removing
     just one fails no test, removing both fails two. Kept as a pair on purpose:
     the reset is about starting clean, this is about ownership, and leaning on
     the reset alone would make a security property depend on a line whose stated
     job is something else. The tests pin the behaviour rather than either
     line. */

  const createIsOurs =
    create.variables !== undefined &&
    sameSession(create.variables.scope, scope);
  const creating = create.isPending && createIsOurs;
  const createError = createIsOurs ? create.error : null;

  const copyLink = async (): Promise<void> => {
    if (!active?.created) return;
    /* The dialog whose URL actually reaches the clipboard, named explicitly so the
       continuation reports against it and not against whatever is on screen when
       the write lands. Without that, a slow write — a permission prompt is enough
       — badges someone else's invite as copied, which is a lie about the
       credential the admin is holding, and they can paste the wrong one. */
    const origin = { dialogId: active.id, scope: active.scope };
    const url = active.created.inviteUrl;
    try {
      await navigator.clipboard.writeText(url);
      patchOperation(origin, { copied: true });
    } catch {
      /* Clipboard access can be refused (insecure context, denied permission).
         The URL is on screen and selectable, so a failed copy is not an error
         state — it just must not claim success. */
      patchOperation(origin, { copied: false });
    }
  };

  const open = active !== null;
  const created = active?.created ?? null;
  const copied = active?.copied ?? false;
  const email = active?.email ?? "";
  const role = active?.role ?? "tenant-member";
  const grantTransformationLead = active?.grantTransformationLead ?? false;
  const emailError = active?.emailError ?? null;

  const dialog = (
    <Modal
      open={open}
      onClose={close}
      title={created ? "Invite created" : "Invite someone by email"}
    >
      {created ? (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-pb-text">
            {created.invitation.email} has been invited as{" "}
            {roleLabel(created.invitation.role)}
            {created.invitation.grantTransformationLead
              ? " and Transformation Lead"
              : ""}
            .
          </p>
          {/* BOTH channels, and the second one is not redundant.

              PB-4922 adds the invitation email, and briefly removed this link on
              the reasoning that an administrator holding a copy is a second
              holder of a single-holder credential. That reasoning assumed the
              email always goes. It does not: a workspace with no configured mail
              sender sends nothing — the send answers `no_sender` and the outbox
              row dead-letters — and with the link gone there was then NO way to
              get the invitation to the person it was issued to. The capability
              this restores is the one Epic-42 describes: the administrator
              passes the link on over a channel the workspace already has.

              Still show-once, exactly as before: only a hash is stored, so this
              cannot be read again. Said plainly, because an admin who closes
              without copying has to revoke and re-invite. */}
          <Alert tone="watch" title="Copy this link now">
            This is the only time the link is shown. If you lose it, revoke the
            invite and create a new one.
          </Alert>
          {/* Look row OK-45: the read-only field takes the filled readonly look. */}
          <Input
            label="Invite link"
            variant="readonly"
            value={created.inviteUrl}
            onFocus={(event) => event.currentTarget.select()}
          />
          {/* Both emails leave through the workspace's own sender. Creating an
              invitation only enqueues asynchronous work, so the CURRENT sending
              setting cannot prove whether that earlier work was delivered. The
              paused branch therefore gives conditional recovery guidance rather
              than rewriting an unknown delivery outcome as a failure.
            *
              THREE branches, not two (PB-5435). "Queued" only once an active
              server is confirmed and sending is on. Paused whenever nothing can
              send, which includes a workspace with no active server whatever
              its stored setting says. And while that is not yet known, the
              paused guidance WITHOUT the pause claim: the unknown answer used
              to fall through to "queued", which told an administrator an email
              was on its way on the strength of a read that had not finished. */}
          {sendingState === "paused" ? (
            <Alert tone="watch" title="Workspace email is paused">
              If {created.invitation.email} has not received the invitation,
              send this link yourself. The set-password email they need
              afterwards also cannot be sent while workspace email remains
              paused.
            </Alert>
          ) : sendingState === "on" ? (
            <p className="text-dense-1 text-pb-text-muted">
              We&apos;ve queued an email with it to {created.invitation.email}.
              If your workspace has no mail sender set up, send this link on
              yourself — and note that the set-password email they need
              afterwards cannot be sent either.
            </p>
          ) : (
            <p className="text-dense-1 text-pb-text-muted">
              If {created.invitation.email} has not received the invitation,
              send this link yourself.
            </p>
          )}
          {/* Its own line so both branches above keep it. */}
          <p className="text-dense-1 text-pb-text-muted">
            The invite stays valid until{" "}
            <AdminTimeZoneLabel
              label={dateTimeOr(created.invitation.expiresAt, "—")}
              formatter={formatter}
              variant="inline"
            />
            .
          </p>
          <div className="flex items-center gap-2">
            <Button variant="primary" onClick={() => void copyLink()}>
              {copied ? "Copied" : "Copy link"}
            </Button>
            <Button variant="secondary" onClick={close}>
              Done
            </Button>
          </div>
        </div>
      ) : (
        /* A REAL form, so Enter submits. `packages/ui`'s Button defaults to
           `type="button"`, so the draft controls had no keyboard submit path at
           all — an administrator typing an address and pressing Enter got
           nothing.

           The form opens no second door around `blockedReason`: the submit button
           carries `disabled`, and a disabled submit button fires no submit event.
           That is untested on purpose — MembersAdmin gates the trigger on
           `=== true`, so a blocked policy disables it and a pending one does too,
           and this dialog is reachable from nowhere else. There is no state in
           which the draft is open AND blocked, so a test for one would be
           asserting something the app cannot produce. */
        <form
          className="flex flex-col gap-4"
          /* `noValidate` because this panel already validates and has its own
             message. Wrapping the controls in a form switched on the browser's
             native constraint validation for `type="email"`, which BLOCKS submit
             before `onSubmit` runs — so the panel's "Enter a valid email
             address" never appeared and a malformed address produced a
             browser-native tooltip instead: different wording in every browser,
             not translatable, and not styled like anything else here. One
             validator, one message. */
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <p className="text-dense-1 text-pb-text-muted">
            For people who aren&apos;t in your directory yet. You&apos;ll get a
            link to share with them.
          </p>
          <Input
            label="Email"
            type="email"
            autoComplete="off"
            placeholder="dana@northwind.io"
            value={email}
            onChange={(event) => {
              patch({ email: event.target.value, emailError: null });
            }}
            {...(emailError ? { error: emailError } : {})}
          />
          <Select
            label="Role"
            value={role}
            onChange={(value) => patch({ role: value as TenantRole })}
            options={ROLE_OPTIONS.map(({ value, label }) => ({ value, label }))}
            hint="Workspace role only. Module access is workspace-wide, and Solve roles are granted inside each Solve."
          />
          <div className="flex items-start gap-2">
            <Checkbox
              aria-label="Transformation Lead"
              checked={grantTransformationLead}
              onChange={(checked) =>
                patch({ grantTransformationLead: checked })
              }
              aria-describedby="invite-transformation-lead-help"
            />
            <div>
              <p className="text-sm font-medium text-pb-text">
                Transformation Lead
              </p>
              <p
                id="invite-transformation-lead-help"
                className="text-dense-1 text-pb-text-muted"
              >
                Grants access to manage LAMA questionnaires, campaigns and
                reports. This is independent of workspace administration.
              </p>
            </div>
          </div>
          {blockedReason === null ? null : (
            /* PB-4922. Relocated out of the removed Select's hint. It has to
               render somewhere: it is the workspace's only explanation for an
               invite the server will refuse, and losing it to a refactor would
               leave an administrator with a 403 and no idea which switch to
               look for. It now also EXPLAINS the disabled button below, rather
               than sitting beside an enabled one. */
            <p className="text-dense-1 text-pb-text-muted">{blockedReason}</p>
          )}
          {/* Where "that person is already a member" lands. The server refuses
              it (an invite for an existing member would be a dead link), and its
              message names the remedy — reactivate a suspended member rather
              than invite them again. */}
          {createError ? (
            <p role="alert" className="text-sm text-pb-red-text">
              {createError.message}
            </p>
          ) : null}
          <div className="flex items-center gap-2">
            {/* Disabled on the POLICY, not only while in flight. Until PB-4922
                this gate lived on the route Select's `local` option; removing
                the Select removed the gate with it, leaving an administrator
                free to type an address and press a fully enabled primary action
                that could only ever end in a refusal. The server still refuses —
                that is the boundary and it is tested — but a control that cannot
                succeed should not invite the attempt. */}
            <Button
              type="submit"
              variant="primary"
              disabled={creating || blockedReason !== null}
            >
              {creating ? "Creating..." : "Create invite"}
            </Button>
            {/* Explicitly `button`: inside a form an unmarked button submits,
                so Cancel would create the invitation it is meant to abandon. */}
            <Button
              type="button"
              variant="secondary"
              onClick={close}
              disabled={creating}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );

  return { openInvite, dialog };
}

export interface PendingInviteCount {
  /** Pending invitations on the first page. */
  readonly count: number;
  /** More pages exist, so `count` is a floor — render it as "N+". */
  readonly atLeast: boolean;
}

/**
 * The count behind the Invites tab badge.
 *
 * Its own query, not a read of the list's: the list unmounts with its tab panel,
 * and the badge has to be right precisely when the Members tab is showing. It
 * also asks a narrower question — pending only, regardless of what the admin has
 * the list filtered to.
 *
 * No exact total, because no cursor endpoint returns one (see
 * the cursor contract). `atLeast` is how the badge stays honest instead of
 * showing a page size as if it were a total.
 */
export function usePendingInviteCount(): PendingInviteCount {
  const claims = useAuthStore((s) => s.claims);
  const filters = { status: "pending", expiry: "active" } as const;
  const query = useQuery({
    /* A COUNT key, not a page-set key. Sharing one with the list's infinite
       query hands this plain query's data to `getNextPageParam`, which throws on
       `.pages` — see invitesQueryKey. */
    queryKey: invitesCountQueryKey(
      claims?.tenantId,
      filters.status,
      filters.expiry,
    ),
    queryFn: () => listInvites(null, filters),
    // Cosmetic gate only; the endpoint is behind TenantAdminGuard regardless.
    enabled: canAdministerWorkspace(claims),
  });
  /* Auth applies expiry=active before paging, using the same request timestamp
     as row presentation. The first page therefore contains only genuinely live
     pending invitations; `nextCursor` still makes this a bounded N+ count, not
     a globally exact total. */
  const rows = query.data?.invitations ?? [];
  return {
    count: rows.length,
    atLeast: query.data !== undefined && query.data.nextCursor !== null,
  };
}

/** Renders the badge, or nothing when no one is waiting — a "0" badge is noise
    on the many tenants that never invite anybody. */
export function PendingInviteBadge(): ReactElement | null {
  const { count, atLeast } = usePendingInviteCount();
  if (count === 0) return null;
  return (
    <span
      className="ms-1.5 rounded-full bg-pb-card-elevated px-1.5 text-dense-1 font-semibold text-pb-text-secondary"
      // Read out in full, because "3+" alone is not a sentence.
      aria-label={`${count}${atLeast ? " or more" : ""} pending`}
    >
      {count}
      {atLeast ? "+" : ""}
    </span>
  );
}

/**
 * The invitation list, as one tab of the workspace directory.
 *
 * Its own search, status filter and cursor — a sibling of the member list, never
 * merged with it. Both filters reach the DATABASE: a tenant can hold far more
 * invitations than one page, so filtering the loaded rows would quietly search
 * only what had already scrolled by.
 */
export function InvitesTabPanel(): ReactElement {
  const formatter = useDateTimeFormatter();
  const { dateTimeOr } = formatter;
  const claims = useAuthStore((s) => s.claims);
  const queryClient = useQueryClient();
  const scope = scopeFromClaims(claims);
  /* The selected row is FENCED to the session that selected it, like the invite
     dialog above and MembersAdmin's removal confirmation.

     Two consequences if it is not. Across tenants, the open dialog names the
     previous tenant's invited email — disclosed even though the revoke itself
     would fail closed at the server. Within one tenant, a replacement
     administrator can confirm a destructive action the previous session chose,
     against an invitation they never looked at. */
  const [selection, setSelection] = useState<ScopedInvite | null>(null);
  const nextSelectionId = useRef(0);
  const revokeTarget =
    selection && sameSession(selection.scope, scope) ? selection.invite : null;
  const setRevokeTarget = (invite: Invite | null): void => {
    nextSelectionId.current += 1;
    setSelection(
      invite ? { id: nextSelectionId.current, scope, invite } : null,
    );
  };

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  /* Defaults to Pending: "who is waiting on me" is the question an admin opens
     this tab with. Revoked rows are history, one selection away. */
  const [statusFilter, setStatusFilter] = useState<InviteViewFilter>("pending");

  /* Debounced so typing does not fire a request per keystroke, matching the
     member search above it. 300ms is the value that search uses. */
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const filters: InviteFilters = {
    ...(debouncedSearch.trim() ? { query: debouncedSearch.trim() } : {}),
    ...(statusFilter === "pending"
      ? { status: "pending" as const, expiry: "active" as const }
      : statusFilter === "expired"
        ? { status: "pending" as const, expiry: "expired" as const }
        : statusFilter === "revoked"
          ? { status: "revoked" as const }
          : {}),
  };

  const invitePaging = useCursorPagination(
    `${claims?.tenantId ?? "none"}|${JSON.stringify(filters)}`,
    { pageSize: INVITES_VIEW_PAGE_SIZE },
  );
  const invites = useQuery({
    /* Filters are in the KEY, so changing one starts a new page set instead of
       appending a differently-filtered page to the previous one. */
    queryKey: [
      ...invitesQueryKey(claims?.tenantId, filters),
      invitePaging.cursor ?? "first",
    ],
    queryFn: () => listInvites(invitePaging.cursor ?? null, filters),
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[3] === claims?.tenantId
        ? previousData
        : undefined,
    /* Cosmetic only — the route is behind RequireTenantAdmin and the endpoint
       behind TenantAdminGuard. This just avoids a guaranteed 403. */
    enabled: canAdministerWorkspace(claims),
  });
  const inviteRows = invites.data?.invitations ?? [];

  const revoke = useMutation({
    mutationFn: ({ invite }: ScopedInvite) => revokeInvite(invite.invitationId),
    onSuccess: (_result, variables) => {
      /* Keyed on the ORIGIN tenant, not one recomputed from a replacement
         session: the revoke committed against the tenant that submitted it, so
         that tenant's cached views are the stale ones.

         Prefix again: a revoke moves a row between the Pending and Revoked
         filters and changes the badge, so every cached view is now stale. */
      void queryClient.invalidateQueries({
        queryKey: invitesQueryKeyPrefix(variables.scope?.tenantId),
      });
      /* Closing addresses the SELECTION that submitted, by id — not merely the
         scope. Scope alone is not an identity: dismissing this dialog mid-flight
         with Escape and then selecting a different invitation produces a new
         selection with the same scope, and an unconditional close (or a
         scope-only one) would dismiss the admin's new confirmation when the
         earlier request landed. Same class as the create completion above. */
      setSelection((current) =>
        current &&
        current.id === variables.id &&
        sameSession(current.scope, variables.scope)
          ? null
          : current,
      );
    },
  });

  /* The in-flight and failed states belong to the session that started the
     revoke, not to the component — otherwise a revoke begun before a handover
     greys out the new administrator's row and reports its failure over a dialog
     they never opened. */
  const revokeIsOurs =
    revoke.variables !== undefined &&
    sameSession(revoke.variables.scope, scope);
  const revoking = revoke.isPending && revokeIsOurs;
  const revokeError = revokeIsOurs ? revoke.error : null;

  /* Resets the mutation as well as the target, so a previous failure does not
     greet the admin when they open the dialog for a different invite. */
  const closeRevokeDialog = (): void => {
    setRevokeTarget(null);
    revoke.reset();
  };

  /* Three distinct empty states, because "nothing here" has three meanings and
     conflating them sends the admin to the wrong control. A search that matched
     nothing is the admin's own narrowing; an empty PENDING list is a fact about
     the default view (history may still exist under All); an empty ALL list is
     the only one that means nobody has ever been invited. Saying "no invites
     match these filters" under the untouched default would send them adjusting
     filters that were never wrong. */
  const emptyMessage =
    debouncedSearch.trim().length > 0
      ? "No invites match these filters."
      : statusFilter === "pending"
        ? "No pending invites."
        : statusFilter === "expired"
          ? "No expired invites."
          : statusFilter === "revoked"
            ? "No revoked invites."
            : "No one has been invited yet.";

  return (
    <>
      <div className="my-4 rounded-xl border border-pb-border bg-pb-card-elevated/25 p-3">
        <div className="mb-3 flex items-start gap-3 px-1">
          <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-pb-accent/10 text-pb-accent-text">
            <MailPlus className="size-4" aria-hidden="true" />
          </span>
          <div>
            <h2 className="font-semibold text-pb-text">Invitation queue</h2>
            <p className="mt-0.5 text-xs leading-relaxed text-pb-text-muted">
              Track who is waiting to join and clear invitations that are no
              longer needed.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[minmax(0,1fr)_200px]">
          <Input
            label="Search invites"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Email address"
          />
          <Select
            label="Status"
            options={STATUS_FILTER_OPTIONS.map(({ value, label }) => ({
              value,
              label,
            }))}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value as InviteViewFilter)}
          />
        </div>
      </div>

      {/* One disclosure for the whole list: every row's "expires"/"joined"/
          "revoked" instant is in the same zone (PB-5409). */}
      <p className="text-dense-1 text-pb-text-muted">
        <AdminTimeZoneLabel
          label="Times"
          formatter={formatter}
          variant="inline"
        />
      </p>

      <Card className="overflow-hidden">
        <CardBody flush>
          {inviteRows.length > 0 ? (
            <ul className="flex flex-col divide-y divide-pb-border">
              {inviteRows.map((invite) => {
                const presentation = presentationFor(invite.status);
                return (
                  <li
                    key={invite.invitationId}
                    className="flex items-center gap-3 px-4 py-3.5 transition-colors hover:pb-overlay-paint"
                  >
                    <span className="grid size-9 shrink-0 place-items-center rounded-full bg-pb-card-elevated text-pb-text-secondary">
                      <MailPlus className="size-4" aria-hidden="true" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-pb-text">
                        {invite.email}
                      </p>
                      <p className="text-dense-1 text-pb-text-muted">
                        {roleLabel(invite.role)}
                        {invite.grantTransformationLead
                          ? " · Transformation Lead"
                          : ""}
                        {" · invited by "}
                        {invite.createdByName ?? "an admin"}
                        {" · "}
                        {invite.status === "revoked"
                          ? `revoked ${dateTimeOr(invite.revokedAt ?? invite.createdAt, "—")}`
                          : invite.status === "accepted"
                            ? `joined ${dateTimeOr(invite.acceptedAt ?? invite.createdAt, "—")}`
                            : `expires ${dateTimeOr(invite.expiresAt, "—")}`}
                      </p>
                    </div>
                    <StatusPill tone={presentation.tone}>
                      {presentation.label}
                    </StatusPill>
                    {/* Revocable while still pending in storage, which includes
                        an expired invite — expiry is DERIVED for presentation and
                        the row is `pending` underneath, so revoking is how an
                        admin clears a dead row.

                        Named positively rather than as "not revoked": `accepted`
                        is also terminal, and the server refuses a revoke on it as
                        `invitation_not_pending`. Listing the revocable states
                        means the next status this panel learns about cannot
                        inherit a button that would fail. */}
                    {invite.status === "pending" ||
                    invite.status === "expired" ? (
                      <Button
                        variant="ghost"
                        onClick={() => setRevokeTarget(invite)}
                        aria-label={`Revoke invite for ${invite.email}`}
                        /* Scoped to THIS row, like the member table's own pending
                           states: a bare pending flag greys out every row while
                           one revoke is in flight, which reads as the whole list
                           having stopped working. `revoking` is fenced to this
                           session as well, so a request begun before a handover
                           cannot disable a row here either. */
                        disabled={
                          revoking &&
                          revoke.variables?.invite.invitationId ===
                            invite.invitationId
                        }
                      >
                        Revoke
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}

          {/* The three settled states the list itself cannot express. Kept
              distinct so a slow first page never renders as "no invites". */}
          {invites.isPending && invites.isFetching ? (
            <p
              role="status"
              className="px-4 py-6 text-center text-sm text-pb-text-muted"
            >
              Loading invites...
            </p>
          ) : null}
          {invites.error ? (
            <AsyncSection
              status="error"
              errorTitle="Couldn't load the invitations"
              errorDescription={invites.error.message}
              onRetry={() => {
                invites.refetch();
              }}
            >
              {null}
            </AsyncSection>
          ) : null}
          {invites.isSuccess && inviteRows.length === 0 ? (
            <p
              role="status"
              className="px-4 py-6 text-center text-sm text-pb-text-muted"
            >
              {emptyMessage}
            </p>
          ) : null}
          {invitePaging.page > 1 || invites.data?.nextCursor ? (
            <div className="flex justify-end border-t border-pb-border p-3">
              <Pagination
                mode="cursor"
                page={invitePaging.page}
                pageItemCount={inviteRows.length}
                canGoBack={invitePaging.canGoBack}
                hasNext={Boolean(invites.data?.nextCursor)}
                busy={invites.isFetching}
                onPrev={invitePaging.goBack}
                onNext={() => {
                  const cursor = invites.data?.nextCursor;
                  if (cursor) invitePaging.goNext(cursor);
                }}
              />
            </div>
          ) : null}
        </CardBody>
      </Card>

      {/* ConfirmDialog (d2): the destructive confirm, red chip, quiet Cancel. */}
      <ConfirmDialog
        open={revokeTarget !== null}
        onClose={closeRevokeDialog}
        title="Revoke invite?"
        confirmLabel={revoking ? "Revoking..." : "Revoke invite"}
        confirmDisabled={revoking}
        onConfirm={() => {
          if (selection) revoke.mutate(selection);
        }}
      >
        <div className="flex flex-col gap-4">
          <p>
            {revokeTarget?.email} will no longer be able to use their invite
            link. You can invite the same address again afterwards.
          </p>
          {/* Inside the dialog, not in the card behind it: a failed revoke does
              not run onSuccess, so this dialog stays open and would cover an
              error rendered underneath — leaving the admin with a button that
              looks inert. Reachable with two tabs, where the second gets a 409. */}
          {revokeError ? (
            <p role="alert" className="text-sm text-pb-red-text">
              {revokeError.message}
            </p>
          ) : null}
        </div>
      </ConfirmDialog>
    </>
  );
}

export { MailPlus as InviteIcon };
